import * as React from 'react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { transparentize } from 'polished';

import { TreeCollapse } from '../../../tree-collapse';
import { CheckboxInput } from '../../../checkbox-input';
import { SelectField, SelectFieldOptions } from '../../../select-field';
import { COLOR, FONT_SIZE, SPACING, SPEED } from '../../../../packages/constants';
import { Edit } from '../../../../packages/icons';
import { TrackingSettingsTheme, TrackingItemStatus } from '../../tracking-settings-enums';
import { Text } from '../../../text';
import { DataSignature } from '../DataWrapper/data-wrapper';

interface TreeCollapseProps {
    handleUpdateData: (e: DataSignature) => void;
    handleSaveOnChange?: (data: DataSignature, e?: HTMLInputElement) => void;
    organisationId?: string;
    category: any;
    categoryIndex: number;
    data: DataSignature;
    dataIndex: number;
    dropdownData?: SelectFieldOptions;
    dataItem: any;
    isTreeExpanded: boolean;
    showAbbreviations?: boolean;
    editAbbreviationFunction?: (organisationId: string, dataItemId: string, categoryName: string) => void;
    theme?: TrackingSettingsTheme;
    filterValue?: any;
}

interface ListColumnProps {
    alignRight?: boolean;
    dropdownData?: SelectFieldOptions;
}

interface TrackingListItemProps {
    isArchived?: boolean;
}

interface CategoryTitleWrapperProps {
    dropdownData?: SelectFieldOptions;
}

export const TrackingList = styled.ul`
    padding-bottom: ${SPACING.MEDIUM};
`;

export const TrackingListItem = styled.li<TrackingListItemProps>`
    display: flex;
    padding: ${SPACING.XX_SMALL} ${SPACING.MEDIUM} ${SPACING.XX_SMALL} 28px;
    background-color: ${(props) => (props.isArchived ? COLOR.LEGACY.DATAGRID_ARCHIVED_BG : 'none')};
`;

const budgetColumnWidth = '145px';

export const TrackingListColumn = styled.div<ListColumnProps>`
    width: ${(props) =>
        props.dropdownData && props.dropdownData.length > 0
            ? props.alignRight
                ? `calc(${budgetColumnWidth} + ${SPACING.MEDIUM})`
                : `calc(100% - ${budgetColumnWidth} - ${SPACING.MEDIUM})`
            : '100%'};
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.alignRight ? 'flex-end' : 'flex-start')};
`;

const StyledTextLabel = styled.span`
    &&& {
        display: flex;
        margin-left: 0;
        font-size: ${FONT_SIZE.SMALL};
        line-height: ${FONT_SIZE.MEDIUM};
        font-weight: 700;
        color: ${COLOR.MAIN.LABEL_TEXT};
        opacity: 1;
        justify-content: flex-end;
        flex-grow: 1;
        margin-right: 90px;
    }
`;

const StyledCheckbox = styled.input`
    margin: 2px 6px 2px 4px;
`;

const StyledLabel = styled.label`
    &&& {
        color: ${COLOR.MAIN.LABEL_TEXT};
        display: inline;
        font-size: ${FONT_SIZE.SMALL};
        font-weight: 400;
        line-height: ${FONT_SIZE.LARGE};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const EditButton = styled.button`
    min-width: 0px;
    border: 1px solid transparent;
    border-radius: 15px;
    background-color: transparent;
    padding: ${SPACING.SMALL};
    margin-left: ${SPACING.SMALL};
    cursor: pointer;
    transition: border-color ${SPEED.TRANSITION}, background-color ${SPEED.TRANSITION};

    &:hover {
        border-color: ${transparentize(0.8, COLOR.MAIN.LABEL_TEXT)};
        background-color: ${transparentize(0.85, COLOR.MAIN.LABEL_TEXT)};
    }
`;

const StyledEdit = styled(Edit)`
    width: 13px;
    height: 13px;
    fill: ${COLOR.MAIN.LABEL_TEXT};
`;

const NoResultsFound = styled(Text)`
    margin-bottom: 0;
`;

const StyledCheckBoxInput = styled(CheckboxInput)`
    min-width: 0px;
`;

const CategoryCounter = styled.span`
    margin-left: ${SPACING.SMALL};
    white-space: nowrap;
    min-width: 0px;
    font-size: ${FONT_SIZE.X_SMALL};
    font-weight: 400;
`;

const CategoryTitleWrapper = styled.div<CategoryTitleWrapperProps>`
    width: ${(props) => (props.dropdownData && props.dropdownData.length ? '50%' : '100%')};
    display: inline-flex;
    align-items: center;
`;

const regexString = /[^\w-_]/g;
const lowerKebab = (value: string): string => value.replace(regexString, '-').toLowerCase();

const TreeCollapseWrapper = ({
    data,
    dataItem,
    dataIndex,
    category,
    categoryIndex,
    filterValue,
    isTreeExpanded,
    handleUpdateData,
    handleSaveOnChange,
    organisationId,
    dropdownData,
    showAbbreviations,
    editAbbreviationFunction,
    theme,
}: TreeCollapseProps) => {
    const [itemsSelected, setItemsSelected] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const handleParentCheckbox = (category: any, e: any) => {
        let count = 0;
        const tempTrackingData = { ...data };
        tempTrackingData.dataSources.forEach((dataItem: any, i: number) => {
            dataItem.categories.forEach((dataCategory: any, j: number) => {
                if (dataCategory === category) {
                    tempTrackingData.dataSources[i].categories[j].items.forEach((catItem: any) => {
                        if (
                            tempTrackingData.hideArchivedTracking &&
                            catItem.trackingItemStatusCode === TrackingItemStatus.Archived
                        ) {
                            return;
                        } else {
                            catItem.isShown = e.target.checked;
                            if (e.target.checked) {
                                count += 1;
                            } else {
                                count = 0;
                            }
                        }
                    });
                }
            });
        });
        if (handleSaveOnChange) handleSaveOnChange(tempTrackingData, e.target);
        handleUpdateData(tempTrackingData);
        setItemsSelected(count);
    };

    const checkParentCheckboxState = useCallback(
        (hideArchivedTracking: any) => {
            let count = 0;
            const categoryCheckboxId = `checkbox__${lowerKebab(dataItem.name)}-category-${lowerKebab(category.name)}`;
            const categoryCheckboxElement = document.getElementById(categoryCheckboxId);
            const checkedValues: boolean[] = [];

            category.items.forEach((item: any) => {
                if (hideArchivedTracking && item.trackingItemStatusCode === TrackingItemStatus.Archived) {
                    return;
                } else {
                    checkedValues.push(item.isShown);
                    if (item.isShown === true) {
                        count += 1;
                    }
                }
            });
            setItemsSelected(count);

            let firstValue = false;
            // loop over each array index
            for (let i = 0; checkedValues.length > i; i += 1) {
                if (i === 0) {
                    // set first item to check against
                    firstValue = checkedValues[0];
                    // next item to check
                } else {
                    // if its different to the firstValue, set indeterminate
                    if (firstValue !== checkedValues[i]) {
                        // set indeterminate
                        if (categoryCheckboxElement) {
                            // @ts-ignore
                            categoryCheckboxElement.indeterminate = true;
                            break;
                        }
                        // if first item is the same, set to true
                    } else if (firstValue && firstValue === checkedValues[i]) {
                        // set true
                        if (categoryCheckboxElement) {
                            // @ts-ignore
                            categoryCheckboxElement.indeterminate = false;
                        }
                        // otherwise set false
                    } else if (!firstValue && firstValue === checkedValues[i]) {
                        // set false
                        if (categoryCheckboxElement) {
                            // @ts-ignore
                            categoryCheckboxElement.indeterminate = false;
                        }
                    }
                }
            }
            if (categoryCheckboxElement) {
                // @ts-ignore
                categoryCheckboxElement.checked = count === checkedValues.length;
            }
        },
        [category.items, category.name, dataItem.name],
    );

    const countTotal = useCallback(() => {
        if (data.hideArchivedTracking) {
            let counter = 0;
            category.items.forEach((item: any) => {
                if (item.trackingItemStatusCode !== TrackingItemStatus.Archived) {
                    counter += 1;
                }
            });
            setTotalCount(counter);
        } else {
            setTotalCount(category.items.length);
        }
    }, [category.items, data.hideArchivedTracking]);

    const handleCheckbox = (event: any, dataIndex: number, categoryIndex: number) => {
        const tempTrackingData = { ...data };
        tempTrackingData.dataSources[dataIndex].categories[categoryIndex].items.forEach((item: any) => {
            if (item.id === event.id) {
                item.isShown = !item.isShown;
            }
        });

        checkParentCheckboxState(data.hideArchivedTracking);

        if (handleSaveOnChange) handleSaveOnChange(tempTrackingData, event.target);
        handleUpdateData(tempTrackingData);
    };

    const handleBudgetDropdown = (event: any, itemId: string, dataIndex: number, categoryIndex: number) => {
        const tempTrackingData = { ...data };
        const option = tempTrackingData.dataSources[dataIndex].categories[categoryIndex].items.find(
            (x: { id: string }) => x.id === itemId,
        );
        if (!option) return;
        option.budget = event.target.value;
        // tslint:disable-next-line:no-unused-expression
        if (handleSaveOnChange) handleSaveOnChange(tempTrackingData, event.target);
        handleUpdateData(tempTrackingData);
    };

    const handleAbbreviationButton = () => {
        if (editAbbreviationFunction && organisationId) {
            editAbbreviationFunction(organisationId, dataItem.id, category.id);
        }
    };

    const filteredList = category.items.filter((item: any) => {
        if (filterValue.startsWith(' ')) {
            return item.name;
        } else {
            return item.name.toLowerCase().includes(filterValue);
        }
    });

    React.useEffect(() => {
        checkParentCheckboxState(data.hideArchivedTracking);
        countTotal();
    }, [data, checkParentCheckboxState, countTotal]);

    return (
        <TreeCollapse
            dataId={`tree__button__${lowerKebab(dataItem.name)}-category--${lowerKebab(category.name)}`}
            key={`${lowerKebab(dataItem.name)}-category-${lowerKebab(category.name)}`}
            expand={isTreeExpanded}
            theme={theme}
            hideSplit={!dropdownData || (dropdownData && dropdownData.length == 0)}
            parentProp={
                <>
                    <CategoryTitleWrapper dropdownData={dropdownData}>
                        <StyledCheckBoxInput
                            inline
                            id={`checkbox__${lowerKebab(dataItem.name)}-category-${lowerKebab(category.name)}`}
                            dataId={`checkbox__${lowerKebab(dataItem.name)}-category-${lowerKebab(category.name)}`}
                            name={'datasource'}
                            label={`${category.name}`}
                            onChange={(e) => handleParentCheckbox(category, e)}
                            labelMaxWidth={dropdownData && dropdownData.length > 0 ? '15vw' : 'none'}
                            labelOverflow={'hidden'}
                            labelTextOverflow={'ellipsis'}
                        />
                        <CategoryCounter
                            aria-label={`${lowerKebab(dataItem.name)}-category-${lowerKebab(
                                category.name,
                            )}-count--text`}
                        >{`(${itemsSelected} of ${totalCount})`}</CategoryCounter>
                        {showAbbreviations && (
                            <EditButton title={'Edit abbreviations'} onClick={handleAbbreviationButton}>
                                <StyledEdit />
                            </EditButton>
                        )}
                    </CategoryTitleWrapper>
                    {dropdownData && dropdownData.length ? (
                        <CategoryTitleWrapper dropdownData={dropdownData}>
                            <StyledTextLabel title={'Budgets'}>Budgets</StyledTextLabel>
                        </CategoryTitleWrapper>
                    ) : null}
                </>
            }
        >
            <TrackingList>
                {filteredList.length > 0 ? (
                    filteredList
                        .sort((a: any, b: any) => (a.name === 'Unassigned' || a.name > b.name ? 1 : -1))
                        .map((item: any) => (
                            <React.Fragment key={item.id}>
                                {item.trackingItemStatusCode === TrackingItemStatus.Archived &&
                                data.hideArchivedTracking ? null : (
                                    <TrackingListItem
                                        isArchived={item.trackingItemStatusCode === TrackingItemStatus.Archived}
                                    >
                                        <TrackingListColumn dropdownData={dropdownData}>
                                            <StyledCheckbox
                                                type="checkbox"
                                                id={item.id}
                                                data-test-id={`checkbox__${lowerKebab(
                                                    dataItem.name,
                                                )}-category-${lowerKebab(category.name)}--${lowerKebab(item.name)}`}
                                                checked={item.isShown}
                                                name={'datasource'}
                                                onChange={() => handleCheckbox(item, dataIndex, categoryIndex)}
                                                disabled={
                                                    item.trackingItemStatusCode === TrackingItemStatus.Archived &&
                                                    data.hideArchivedTracking
                                                }
                                            />
                                            <StyledLabel htmlFor={item.id} title={item.name}>
                                                {item.name}
                                            </StyledLabel>
                                        </TrackingListColumn>
                                        {dropdownData && dropdownData.length ? (
                                            <TrackingListColumn alignRight dropdownData={dropdownData}>
                                                <SelectField
                                                    id={`select-${item.id}`}
                                                    onChange={(e) =>
                                                        handleBudgetDropdown(e, item.id, dataIndex, categoryIndex)
                                                    }
                                                    name={'budget'}
                                                    dataId={`dropdown__${lowerKebab(
                                                        dataItem.name,
                                                    )}-category-${lowerKebab(category.name)}--${lowerKebab(item.name)}`}
                                                    options={dropdownData}
                                                    placeholder={'-'}
                                                    disabled={!item.isShown}
                                                    value={item.budget || ''}
                                                    emphasize
                                                    minWidth={'130px'}
                                                    maxWidth={'130px'}
                                                />
                                            </TrackingListColumn>
                                        ) : null}
                                    </TrackingListItem>
                                )}
                            </React.Fragment>
                        ))
                ) : (
                    <li>
                        <NoResultsFound color={COLOR.MAIN.FORM_FIELD_DARK} fontSize={'14px'} indent={'40px'}>
                            No results found.
                        </NoResultsFound>
                    </li>
                )}
            </TrackingList>
        </TreeCollapse>
    );
};

export default TreeCollapseWrapper;
